import React, { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  showCloseButton?: boolean;
  noPadding?: boolean;
  fullHeight?: boolean;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  full: 'max-w-[95vw]',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  noPadding = false,
  fullHeight = false,
}) => {
  // Block body scroll when modal is open
  useEffect(() => {
    if (typeof globalThis === 'undefined' || !globalThis.document) return;

    try {
      if (isOpen) {
        globalThis.document.body.style.overflow = 'hidden';
      } else {
        globalThis.document.body.style.overflow = '';
      }
    } catch (error) {
      console.error('Failed to update body overflow:', error);
    }

    return () => {
      try {
        if (globalThis.document?.body) {
          globalThis.document.body.style.overflow = '';
        }
      } catch (error) {
        console.error('Failed to cleanup body overflow:', error);
      }
    };
  }, [isOpen]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-soft" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel
                className={`w-full ${sizeClasses[size]} ${fullHeight ? 'max-h-[90vh]' : ''} transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-soft-lg border border-neutral-200/80 transition-all ${fullHeight ? 'flex flex-col' : ''}`}
              >
                {noPadding ? (
                  children
                ) : (
                  <div className="px-6 py-5">{children}</div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
